import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import {
  getAllProducts,
  getCompany_Info,
  getSeedCategories,
} from "../Services/client";
import { useTranslation } from "react-i18next";

const AppContext = createContext();

export const AppContextProvider = ({ children }) => {
  const [context_products, setContext_Products] = useState([]);
  const [context_categories, setContext_Categories] = useState([]);
  const [context_companyinfo, setContext_CompanyInfo] = useState([]);
  const [context_userAddress, setContext_userAddress] = useState([]);
  const [cartCount, setCartCount] = useState(0);
  const { i18n } = useTranslation();

  const groupProductsWithImages = useCallback((data) => {
    const grouped = {};
    data.forEach((row) => {
      const productId = row.product_id;
      if (!grouped[productId]) {
        grouped[productId] = {
          id: row.product_id,
          name: row.product_title,
          description: row.product_desc,
          product_subtitle: row.product_subtitle || "",
          product_desc2: row.product_desc2 || "",
          category_id: row.category_id,
          images: [],
          detail: [],
          price: row?.price,
          unit: row?.unit,
          discount: row?.discount,
          avail_qty: row?.avail_qty,
          seller: [
            {
              seller_name: row?.seller_name,
              seller_address: row?.seller_address,
              seller_city: row?.seller_city,
              seller_country: row?.seller_country,
              seller_email: row?.seller_email,
              seller_website: row?.seller_website,
              seller_id: row?.seller_id,
              seller_phone: row?.seller_phone,
              seller_status: row?.seller_status,
            },
          ],
        };
      }
      // Populate all unit options from the JSON array the API sends
      if (grouped[productId].detail.length === 0 && row.all_details) {
        const details = typeof row.all_details === "string"
          ? JSON.parse(row.all_details)
          : row.all_details;
        grouped[productId].detail = details;
      }

      if (row.image_ids) {
        const ids = String(row.image_ids).split("||");
        const urls = String(row.pro_imgs || "").split("||");
        ids.forEach((id, idx) => {
          if (!id) return;
          grouped[productId].images.push({
            id,
            url: urls[idx],
          });
        });
      }
    });
    return Object.values(grouped);
  }, []);

  const fetchData = useCallback(async (lng) => {
    const [companyInfo, catResponse, prodResponse] = await Promise.all([
      getCompany_Info(),
      getSeedCategories(lng),
      getAllProducts({ lng, page: 1, limit: 10000, categoryId: "", search: "" }),
    ]);

    if (companyInfo?.data?.data?.length && companyInfo.status === 200) {
      setContext_CompanyInfo(companyInfo.data.data);
    }

    if (catResponse?.data?.data?.length && catResponse.status === 200) {
      setContext_Categories(catResponse.data.data);
    }

    if (prodResponse?.data?.data?.length && prodResponse.status === 200) {
      const groupedProducts = groupProductsWithImages(prodResponse.data.data);
      setContext_Products(groupedProducts);
    }
  }, [groupProductsWithImages]);

  useEffect(() => {
    loadCart();
    if (context_categories.length === 0 && context_products.length === 0) {
      const lng = localStorage.getItem("i18nextLng");
      if (lng !== "en" && lng !== "hi") {
        i18n.changeLanguage("en");
      }
      fetchData(lng);
    }
  }, []);

  const loadCart = () => {
    const cartItems = localStorage.getItem("cartItems");
    if (cartItems !== "undefined" && cartItems !== undefined) {
      const stored = JSON.parse(cartItems) || [];

      const totalQty = stored.reduce((sum, item) => sum + (item.qty || 0), 0);
      setCartCount(totalQty);
      return stored;
    } else {
      return [];
    }
  };

  const addToCart = useCallback((newItem) => {
    const cartItems = localStorage.getItem("cartItems");
    if (cartItems !== "undefined" && cartItems !== undefined) {
      const existingCart = JSON.parse(cartItems) || [];

      const index = existingCart?.findIndex(
        (item) =>
          item.productId === newItem.productId &&
          item.detailId === newItem.detailId
      );

      if (index !== -1) {
        existingCart[index].qty = newItem.qty;
        existingCart[index].price = newItem.price;
        // existingCart[index].unit = newItem.unit;
        existingCart[index].detailId = newItem.detailId;
      } else {
        existingCart.push(newItem);
      }

      localStorage.setItem("cartItems", JSON.stringify(existingCart));

      const totalQty = existingCart.reduce(
        (sum, item) => sum + (item.qty || 0),
        0
      );
      setCartCount(totalQty);
    }
  }, []);

  const contextValue = useMemo(() => ({
    context_products,
    context_categories,
    context_companyinfo,
    cartCount,
    addToCart,
    loadCart,
    setCartCount,
    fetchData,
    groupProductsWithImages,
  }), [context_products, context_categories, context_companyinfo, cartCount, addToCart, loadCart, fetchData, groupProductsWithImages]);

  return (
    <AppContext.Provider value={contextValue}>
      {children}
    </AppContext.Provider>
  );
};

export const ApplicationContext = () => useContext(AppContext);
